import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown } from 'react-bootstrap';
import { types as coreTypes } from '@sitebuilder/core';
import { StateShape as ReduxStateShape } from '../../reducers';
import { actionCreators as envActionCreators } from '../../reducers/envDiff';
import {
    filterIconActiveSvg,
    filterIconSvg,
    chevronUpSvg,
    chevronDownSvgTwo,
} from '../../assets/images';
import UserFilterInput from '../BaseComponents/UserFilterInput';
import { SpinnerIcon } from '../BaseComponents';
import { EnvSync } from '../../types';
import DiffItem from './DiffItem';

const initialSort = { field: '', dir: '' };

const DiffList = () => {
    const dispatch = useDispatch();
    const [selectedItems, setSelectedItems] = useState<EnvSync[]>([]);
    const [enableSrctoDest, setEnableSrctoDest] = useState<boolean>(false);
    const [enableDesttoSrc, setEnableDesttoSrc] = useState<boolean>(false);
    const {
        listItems,
        // listLoadError,
        // listLoading,
        sourceEnv,
        destEnv,
        diffItems,
        isMergeSuccessful,
        mergingMessage,
        diffLoading,
    } = useSelector((state: ReduxStateShape) => state.envDiff);
    const previousValues = useRef({ sourceEnv, destEnv });

    const [initialData, setInitialData] = useState<any[]>([]);
    const [activeFolders, setActiveFolders] = useState<string[]>([]);

    const [nameFilter, setNameFilter] = useState<string>('');
    const [showNameFilter, setShowNameFilter] = useState<boolean>(false);
    const [showSrcAuthorFilter, setShowSrcAuthorFilter] = useState<boolean>(false);
    const [showDestAuthorFilter, setShowDestAuthorFilter] = useState<boolean>(false);
    const [authorsType, setAuthorsType] = useState<string>('');
    const [authorsInArray, setAuthorsInArray] = useState<any>([]);
    const [sort, setSort] = useState(initialSort);
    const [isSrcDDOpen, setIsSrcDDOpen] = useState(false);
    const [isDestDDOpen, setIsDestDDOpen] = useState(false);
    const [selectedSrcItem, setSelectedSrcItem] = useState<coreTypes.EnvListItem | null>(null);
    const [selectedSDestItem, setSelectedDestItem] = useState<coreTypes.EnvListItem | null>(null);
    const [isSameFolderSelected, setIsSameFolderSelected] = useState<Boolean>(false);

    const nameValueRef = useRef(true);
    const srcAuthorFilterIconRef = useRef<any>(null);
    const destAuthorFilterIconRef = useRef<any>(null);

    useEffect(() => {
        if (listItems === null) {
            dispatch(envActionCreators.listEnvironments());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listItems]);

    useEffect(() => {
        const srcFolder = listItems?.find(
            (item: coreTypes.EnvListItem) => item?.id === sourceEnv
        )?.path;
        const destFolder = listItems?.find(
            (item: coreTypes.EnvListItem) => item?.id === destEnv
        )?.path;

        if (
            sourceEnv &&
            destEnv &&
            srcFolder !== destFolder &&
            (previousValues.current.destEnv !== destEnv ||
                previousValues.current.sourceEnv !== sourceEnv)
        ) {
            setIsSameFolderSelected(false);
            dispatch(envActionCreators.listDiff(sourceEnv, destEnv));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }

        if (sourceEnv && destEnv && srcFolder === destFolder) {
            setIsSameFolderSelected(true);
            setInitialData([]);
        }
    }, [sourceEnv, destEnv, dispatch, listItems]);

    useEffect(() => {
        if (diffItems && diffItems.length > 0) {
            let _diffItems: any[] = diffItems;
            const foldersToInclude: any[] = [];

            const addFolderToArray = (filePath: string) => {
                const folder = filePath.substring(0, filePath.lastIndexOf('/'));
                // adds folder path to foldersToInclude array
                !foldersToInclude.includes(folder) && foldersToInclude.push(folder);
            };

            if (!nameValueRef?.current) {
                _diffItems = _diffItems.filter((d) => {
                    const isTrue = d.key.toLowerCase().includes(nameFilter.toLowerCase() || '');
                    isTrue && !d.isFolder && addFolderToArray(d.key);
                    return isTrue || d.isFolder;
                });
            }

            if (authorsInArray.length && authorsType) {
                _diffItems = _diffItems.filter((d) => {
                    const isTrue = authorsInArray.includes(d[authorsType]?.author);
                    isTrue && !d.isFolder && addFolderToArray(d.key);
                    return isTrue || d.isFolder;
                });
            }

            // Generates an array with a list of all its parent folders
            const fullListOfFoldersToInclude = foldersToInclude.reduce((acc, folder) => {
                const parentFolders = folder.split('/').splice(0, folder.split('/').length - 1);
                parentFolders.forEach((r: string) => {
                    if (r) {
                        !acc.includes(`/${r}`) && acc.push(`/${r}`);
                    }
                });
                acc.push(folder);
                return acc;
            }, []);

            if (fullListOfFoldersToInclude.length) {
                _diffItems = _diffItems.filter(
                    (d) => !d.isFolder || fullListOfFoldersToInclude.includes(d.key)
                );
            }

            if (sort.field && sort.dir) {
                const value1 = sort.dir === 'asc' ? -1 : 1;
                const value2 = sort.dir === 'asc' ? 1 : -1;

                _diffItems = [..._diffItems].sort((a: any, b: any) => {
                    switch (sort.field) {
                        case 'Id':
                            return b.key < a.key ? value1 : b.key > a.key ? value2 : 0;
                        case 'SrcDate':
                            return b.src?.lastModifiedDate < a.src?.lastModifiedDate
                                ? value1
                                : b.src?.lastModifiedDate > a.src?.lastModifiedDate
                                  ? value2
                                  : 0;
                        case 'DestDate':
                            return b.dest?.lastModifiedDate < a.dest?.lastModifiedDate
                                ? value1
                                : b.dest?.lastModifiedDate > a.dest?.lastModifiedDate
                                  ? value2
                                  : 0;
                        default:
                            return 1;
                    }
                });
            }

            const processedItems = getFoldersTree(_diffItems)?.children;

            setInitialData(processedItems);
        } else {
            setInitialData([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diffItems, nameFilter, authorsInArray, sort]);

    useEffect(() => {
        if (selectedItems.length) {
            const leftOnlyLength = selectedItems.filter(
                (i: EnvSync) => i.msg === 'Left only'
            )?.length;
            const rightOnlyLength = selectedItems.filter(
                (i: EnvSync) => i.msg === 'Right only'
            )?.length;

            if (leftOnlyLength && rightOnlyLength) {
                setEnableDesttoSrc(false);
                setEnableSrctoDest(false);
                return;
            }

            if (leftOnlyLength) {
                setEnableSrctoDest(true);
                setEnableDesttoSrc(false);
                return;
            }

            if (rightOnlyLength) {
                setEnableDesttoSrc(true);
                setEnableSrctoDest(false);
                return;
            }

            setEnableDesttoSrc(true);
            setEnableSrctoDest(true);
        } else {
            setEnableDesttoSrc(false);
            setEnableSrctoDest(false);
        }
    }, [selectedItems]);

    useEffect(() => {
        if (isMergeSuccessful) {
            dispatch(envActionCreators.listDiff(sourceEnv, destEnv));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMergeSuccessful]);

    useEffect(() => {
        setSelectedItems([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [diffItems]);

    const onSelectItem = (data: EnvSync) => {
        const index = selectedItems.findIndex((obj: EnvSync) => obj.key === data.key);
        if (index < 0) {
            const items: EnvSync[] = [...selectedItems, data];
            setSelectedItems(items);
        } else {
            const items: EnvSync[] = [...selectedItems];
            items.splice(index, 1);
            setSelectedItems(items);
        }
    };

    const onClickFolder = (path: string) => {
        const index = activeFolders.findIndex((d: string) => d === path);
        if (index < 0) {
            const items: string[] = [...activeFolders, path];
            setActiveFolders(items);
        } else {
            const items: string[] = [...activeFolders];
            items.splice(index, 1);
            setActiveFolders(items);
        }
    };

    const onClickMerge = async (type: string) => {
        if (sourceEnv && destEnv) {
            if (type === 'srcToDest') {
                dispatch(
                    envActionCreators.merge(
                        sourceEnv,
                        destEnv,
                        selectedItems.map((i: EnvSync) => ({ ...i, key: `/${i.key}` }))
                    )
                );
            } else {
                dispatch(
                    envActionCreators.merge(
                        destEnv,
                        sourceEnv,
                        selectedItems.map((i: EnvSync) => ({
                            ...i,
                            srcVersion: i.destVersion,
                            destVersion: i.srcVersion,
                            key: `/${i.key}`,
                        }))
                    )
                );
            }
        }
    };

    const setSourceEnv = async (id: string) => {
        setSelectedSrcItem(
            listItems?.find((item: coreTypes.EnvListItem) => item?.id === id) || null
        );
        dispatch(envActionCreators.setSourceEnv(id));
    };

    const setDestEnv = async (id: string) => {
        setSelectedDestItem(
            listItems?.find((item: coreTypes.EnvListItem) => item?.id === id) || null
        );
        dispatch(envActionCreators.setDestinationEnv(id));
    };

    // get formatted data from env diff items
    const getFoldersTree = (items: coreTypes.EnvDiff[]) => {
        const root: any = {};
        for (const { key, ...rest } of items) {
            const matchedKeys = key?.match(/[^\/]+/g);
            let index = 0;
            matchedKeys?.reduce((acc: any, folder: any) => {
                if (!acc.children) acc.children = {};
                const path = matchedKeys?.slice(0, index + 1)?.join('/');
                index++;
                return (
                    acc.children[folder] ||
                    (acc.children[folder] = {
                        key: folder,
                        folderPath: path,
                        ...rest,
                    })
                );
            }, root);
        }

        (function recurse(node: any) {
            if (!node.children) return;
            node.children = Object.values(node.children);
            node.children?.forEach(recurse);
        })(root);

        return root;
    };

    const handleSelectUser = (selectedUsers: any, type: string) => {
        if (authorsType !== type) {
            setAuthorsType(type);
        }

        const delayDebounceFn = setTimeout(() => {
            const authorsIn = selectedUsers;
            setAuthorsInArray(authorsIn);
            // send request for search
        }, 1000);

        return () => clearTimeout(delayDebounceFn);
    };

    return (
        <div className="diff-list">
            <div className="merge-buttons">
                <div className="dropdown-container">
                    <label>Source</label>
                    <Dropdown
                        className="dropdown"
                        onToggle={(isOpen: boolean) => setIsSrcDDOpen(isOpen)}
                        onSelect={(value: any) => setSourceEnv(value)}
                    >
                        <Dropdown.Toggle className="dropdown-toggle src-dropdown-toggle">
                            <>
                                {selectedSrcItem ? (
                                    <span className="selected-value">
                                        {selectedSrcItem?.id} ({selectedSrcItem?.path})
                                    </span>
                                ) : (
                                    <span className="placeholder">Select source</span>
                                )}
                                <i
                                    className={`dropdown-toggle-icon fas ${
                                        isSrcDDOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                                    }`}
                                />
                            </>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="dropdown-menu src-dropdown-menu">
                            {listItems?.map((item: coreTypes.EnvListItem, index: number) => (
                                <Dropdown.Item
                                    className="dropdown-item"
                                    key={index}
                                    eventKey={item.id}
                                >
                                    {item.id}({item.path})
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="dropdown-container">
                    <label>Destination</label>
                    <Dropdown
                        className="dropdown"
                        onToggle={(isOpen: boolean) => setIsDestDDOpen(isOpen)}
                        onSelect={(value: any) => setDestEnv(value)}
                    >
                        <Dropdown.Toggle className="dropdown-toggle dest-dropdown-toggle">
                            <>
                                {selectedSDestItem ? (
                                    <span className="selected-value">
                                        {selectedSDestItem?.id} ({selectedSDestItem?.path})
                                    </span>
                                ) : (
                                    <span className="placeholder">Select destination</span>
                                )}
                                <i
                                    className={`dropdown-toggle-icon fas ${
                                        isDestDDOpen ? 'fa-chevron-up' : 'fa-chevron-down'
                                    }`}
                                />
                            </>
                        </Dropdown.Toggle>

                        <Dropdown.Menu className="dropdown-menu dest-dropdown-menu">
                            {listItems?.map((item: coreTypes.EnvListItem, index: number) => (
                                <Dropdown.Item
                                    className="dropdown-item"
                                    key={index}
                                    eventKey={item.id}
                                >
                                    {item.id}({item.path})
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="button-container">
                    <button
                        className={`merge-button ${!enableSrctoDest ? 'disabled' : ''}`}
                        onClick={() => enableSrctoDest && onClickMerge('srcToDest')}
                    >
                        Merge SRC to DEST
                    </button>
                    <button
                        className={`merge-button ${!enableDesttoSrc ? 'disabled' : ''}`}
                        onClick={() => enableDesttoSrc && onClickMerge('destToSrc')}
                    >
                        Merge DEST to SRC
                    </button>
                </div>
                <p
                    className={`${
                        isMergeSuccessful === null
                            ? 'loading-msg'
                            : isMergeSuccessful
                              ? 'success-msg'
                              : 'failed-msg'
                    }`}
                >
                    {mergingMessage}
                </p>
            </div>
            {diffLoading ? (
                <SpinnerIcon iconClassName="icon" iconName="fa-spinner" />
            ) : (
                <>
                    {isSameFolderSelected ? (
                        <div>
                            <i className={`error-icon fas fa-exclamation-circle`} />
                            <span className={'error-message'}>
                                Error: selected envs has same folders hence cannot be merged. Please
                                select different folders.
                            </span>
                        </div>
                    ) : null}
                    {initialData && initialData?.length > 0 ? (
                        <table className="diff-table items-table">
                            <thead>
                                <tr>
                                    <th>
                                        {showNameFilter ? (
                                            <input
                                                id="name-filter-input"
                                                type="text"
                                                value={nameFilter}
                                                onChange={(e) => {
                                                    if (nameValueRef?.current) {
                                                        nameValueRef.current = false;
                                                    }
                                                    setNameFilter(e.target.value);
                                                }}
                                                placeholder={`Filter By File Name`}
                                                autoFocus
                                            />
                                        ) : (
                                            'File Name'
                                        )}
                                        <div className={'header-buttons'}>
                                            <button
                                                className={'sort-button'}
                                                onClick={() => {
                                                    setSort({
                                                        field: 'Id',
                                                        dir: sort.dir === 'asc' ? 'desc' : 'asc',
                                                    });
                                                }}
                                            >
                                                {sort.field === 'Id' ? (
                                                    <img
                                                        className={'sort-img-one'}
                                                        src={
                                                            sort.dir === 'desc'
                                                                ? chevronDownSvgTwo
                                                                : chevronUpSvg
                                                        }
                                                        alt={`Sort By File Name}`}
                                                    />
                                                ) : (
                                                    <>
                                                        <img
                                                            className={'sort-img'}
                                                            src={chevronUpSvg}
                                                            alt={`Sort By File Name}`}
                                                        />
                                                        <img
                                                            className={'sort-img'}
                                                            src={chevronDownSvgTwo}
                                                            alt={`Sort By File Name}`}
                                                        />
                                                    </>
                                                )}
                                            </button>
                                            <img
                                                className={'filter-img'}
                                                src={
                                                    showNameFilter
                                                        ? filterIconActiveSvg
                                                        : filterIconSvg
                                                }
                                                alt={`Filter File Name}`}
                                                onClick={() => {
                                                    setShowNameFilter(!showNameFilter);
                                                    setNameFilter('');
                                                }}
                                            />
                                        </div>
                                    </th>
                                    <th>Folder</th>
                                    <th>Comparison</th>
                                    <th>
                                        <UserFilterInput
                                            display={showSrcAuthorFilter}
                                            onSelectUser={(e) => handleSelectUser(e, 'src')}
                                            closeDropdown={() => setShowSrcAuthorFilter(false)}
                                            toggleIconRef={srcAuthorFilterIconRef}
                                        />
                                        <div
                                            style={{
                                                display: !showSrcAuthorFilter
                                                    ? 'inline-block'
                                                    : 'none',
                                            }}
                                        >
                                            {'Src Author'}
                                        </div>
                                        <div className={'header-buttons'}>
                                            <img
                                                ref={srcAuthorFilterIconRef}
                                                id={'user-filter-img'}
                                                className={'filter-img'}
                                                src={
                                                    showSrcAuthorFilter
                                                        ? filterIconActiveSvg
                                                        : filterIconSvg
                                                }
                                                alt={`Filter By Source Author`}
                                                onClick={() => {
                                                    setShowSrcAuthorFilter(!showSrcAuthorFilter);
                                                    setShowDestAuthorFilter(false);
                                                }}
                                            />
                                        </div>
                                    </th>
                                    <th>
                                        <UserFilterInput
                                            display={showDestAuthorFilter}
                                            onSelectUser={(e) => handleSelectUser(e, 'dest')}
                                            closeDropdown={() => setShowDestAuthorFilter(false)}
                                            toggleIconRef={destAuthorFilterIconRef}
                                        />
                                        <div
                                            style={{
                                                display: !showDestAuthorFilter
                                                    ? 'inline-block'
                                                    : 'none',
                                            }}
                                        >
                                            {'Dest Author'}
                                        </div>
                                        <div className={'header-buttons'}>
                                            <img
                                                ref={destAuthorFilterIconRef}
                                                id={'user-filter-img'}
                                                className={'filter-img'}
                                                src={
                                                    showDestAuthorFilter
                                                        ? filterIconActiveSvg
                                                        : filterIconSvg
                                                }
                                                alt={`Filter By Destination Author`}
                                                onClick={() => {
                                                    setShowDestAuthorFilter(!showDestAuthorFilter);
                                                    setShowSrcAuthorFilter(false);
                                                }}
                                            />
                                        </div>
                                    </th>
                                    <th>
                                        {'Src Date'}
                                        <button
                                            className={'sort-button'}
                                            onClick={() =>
                                                setSort({
                                                    field: 'SrcDate',
                                                    dir: sort.dir === 'asc' ? 'desc' : 'asc',
                                                })
                                            }
                                        >
                                            {sort.field === 'SrcDate' ? (
                                                <img
                                                    className={'sort-img-one'}
                                                    src={
                                                        sort.dir === 'desc'
                                                            ? chevronDownSvgTwo
                                                            : chevronUpSvg
                                                    }
                                                    alt={`Sort By Src Date`}
                                                />
                                            ) : (
                                                <>
                                                    <img
                                                        className={'sort-img'}
                                                        src={chevronUpSvg}
                                                        alt={`Sort By Src Date`}
                                                    />
                                                    <img
                                                        className={'sort-img'}
                                                        src={chevronDownSvgTwo}
                                                        alt={`Sort By Src Date`}
                                                    />
                                                </>
                                            )}
                                        </button>
                                    </th>
                                    <th>
                                        {'Dest Date'}
                                        <button
                                            className={'sort-button'}
                                            onClick={() =>
                                                setSort({
                                                    field: 'DestDate',
                                                    dir: sort.dir === 'asc' ? 'desc' : 'asc',
                                                })
                                            }
                                        >
                                            {sort.field === 'DestDate' ? (
                                                <img
                                                    className={'sort-img-one'}
                                                    src={
                                                        sort.dir === 'desc'
                                                            ? chevronDownSvgTwo
                                                            : chevronUpSvg
                                                    }
                                                    alt={`Sort By Dest Date`}
                                                />
                                            ) : (
                                                <>
                                                    <img
                                                        className={'sort-img'}
                                                        src={chevronUpSvg}
                                                        alt={`Sort By Dest Date`}
                                                    />
                                                    <img
                                                        className={'sort-img'}
                                                        src={chevronDownSvgTwo}
                                                        alt={`Sort By Dest Date`}
                                                    />
                                                </>
                                            )}
                                        </button>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {initialData?.map((item: any, i: number) => (
                                    <DiffItem
                                        key={i}
                                        item={item}
                                        activeFolders={activeFolders}
                                        onClickFolder={(path) => onClickFolder(path)}
                                        onSelectItem={(data) => onSelectItem(data)}
                                        selectedItems={selectedItems}
                                    />
                                ))}
                            </tbody>
                        </table>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default DiffList;
